/* eslint-disable react-hooks/rules-of-hooks */
import {Log} from '@aboutyou/mlm';
import {getCookieValue} from '@aboutyou/utils';
import {useCallback, useEffect} from 'react';
import {getIsStorageConsentGiven} from '../utils/consentBannerAcceptedState';
import {
  EMPTY_VALUE,
  useRawCookieValue__PRIVATE,
  useRawValueStore,
  useStoredValue,
  useTransformedValueStore,
  useUpdateValue,
} from './CookieContext';

export const useCookie = <Value>({
  cookieName,
  defaultValue,
  transformRawValue,
  config,
  forcePersistValue_ILLEGALLY = false,
}: {
  cookieName: string;
  defaultValue: Value;
  transformRawValue: (value: string) => Value | undefined;
  config?: {domain?: string; path?: string; expirationInDays?: number};
  /** Setting this to true will force save the value in cookie even when there is no cookie consent for doing so.
   * This is in general a legal threat and can only be used if agreed with the legal department.
   * It will be mandatory to adjust the consent banner text for all countries before the change can go online.
   * Please use this only if it's strictly mandatory to make the ABOUT YOU website work
   */
  forcePersistValue_ILLEGALLY?: boolean;
}) => {
  // eslint-disable-next-line no-restricted-syntax
  const cookieRawValue = useRawCookieValue__PRIVATE(cookieName);
  const transformedValue = getTransformRawValue({
    cookieName,
    rawValue: cookieRawValue,
    transformRawValue,
  });

  if (!process.browser) {
    return [
      transformedValue ?? defaultValue,
      () => {
        throw new Error('useCookie update could only be called in the browser');
      },
    ] as const;
  }

  const storeValue = useStoredValue(cookieName) as Value | undefined;
  const updateCache = useUpdateValue();

  let currentValue;
  if (storeValue === EMPTY_VALUE) {
    currentValue = undefined;
  } else {
    currentValue = storeValue ?? transformedValue ?? defaultValue;
  }

  const update = useCallback(
    (nextValue: Value | undefined) => {
      updateCache({
        cookieName,
        nextValue,
        config,
        forcePersistValue_ILLEGALLY,
      });
    },
    [config, cookieName, forcePersistValue_ILLEGALLY, updateCache],
  );

  return [currentValue, update] as const;
};

const _readValues = new Map();
/**
 * This hook can be used to read the value from store with a fallback to reading directly from document.cookies.
 * Use this hook only if you need to read the value in a callback event (ex: Button Click) and not in a render function.
 * Use the regular `useCookie` hook if the value affects what is rendered.
 *
 * This hooks keeps a cache of the value read from cookie (in _readValues), so that it doesn't need to read from document.cookies multiple times.
 */
export const useCookieValueCallback = <Value>({
  cookieName,
  defaultValue,
  transformRawValue,
}: {
  cookieName: string;
  defaultValue: Value;
  transformRawValue: (value: string) => Value | undefined;
}) => {
  const rawValueStore = useRawValueStore();
  const transformedValueStore = useTransformedValueStore();

  return useCallback(() => {
    if (!process.browser) {
      const transformedValue = getTransformRawValue({
        cookieName,
        rawValue: rawValueStore.getState().store[cookieName],
        transformRawValue,
      });
      return transformedValue ?? defaultValue;
    }

    const storeValue = transformedValueStore.getState().store[cookieName];
    if (storeValue) {
      return storeValue === EMPTY_VALUE
        ? undefined
        : storeValue ?? defaultValue;
    }

    if (_readValues.has(cookieName)) {
      return _readValues.get(cookieName);
    }

    const valueFromBrowserCookies = getIsStorageConsentGiven()
      ? getCookieValue(cookieName)
      : undefined;
    const transformedValue = getTransformRawValue({
      cookieName,
      rawValue: valueFromBrowserCookies,
      transformRawValue,
    });
    _readValues.set(cookieName, transformedValue);

    return _readValues.get(cookieName) ?? defaultValue;
  }, [
    transformedValueStore,
    cookieName,
    transformRawValue,
    defaultValue,
    rawValueStore,
  ]);
};

/**
 * Use this hook if you only need to update the value and you don't need the updated value.
 * This hook helps to avoid unnecessary re-renders due to the updated value when you only need to update the value.
 */
export const useCookieUpdateCallback = <Value>({
  cookieName,
  config,
  forcePersistValue_ILLEGALLY,
}: {
  cookieName: string;
  config?: {domain?: string; path?: string; expirationInDays?: number};
  forcePersistValue_ILLEGALLY?: boolean;
}) => {
  const updateCache = useUpdateValue();

  return useCallback(
    (nextValue: Value | undefined) => {
      updateCache({
        cookieName,
        nextValue,
        config,
        forcePersistValue_ILLEGALLY,
      });
    },
    [config, cookieName, forcePersistValue_ILLEGALLY, updateCache],
  );
};

/**
 * This hook returns the current value from the store when its mounted but the value is stale since any updates to the cookie will not be reflected or make the hook rerender.
 * This hook is similar to `useCookieValueCallback` in the sense it doesn't rerender when the value is updated, but has a very unique purpose when used in combination with `useCookieUpdateEffect`.
 */
export const useStaleCookieValue = <Value>({
  cookieName,
  defaultValue,
  transformRawValue,
}: {
  cookieName: string;
  defaultValue: Value;
  transformRawValue: (value: string) => Value | undefined;
}) => {
  const rawValueStore = useRawValueStore();
  const transformedValueStore = useTransformedValueStore();

  const transformedValue = getTransformRawValue({
    cookieName,
    rawValue: rawValueStore.getState().store[cookieName],
    transformRawValue,
  });
  if (!process.browser) {
    return transformedValue ?? defaultValue;
  }

  const storeValue = transformedValueStore.getState().store[cookieName] as
    | Value
    | undefined;

  if (storeValue === EMPTY_VALUE) {
    return undefined;
  } else {
    return storeValue ?? transformedValue ?? defaultValue;
  }
};

/**
 * This effect allows you to listen to updates to the cookie value without triggering a rerender of the component where its used.
 * Using `useCookieUpdateEffect` together with `useStaleCookieValue` lets you have the correct cookie value on mount of a component + listen to updates to the cookie value without triggering a rerender.
 */
export function useCookieUpdateEffect<Value>(
  cookieName: string,
  callback: (cookieValue: Value | undefined) => void,
) {
  const useStoreBase = useTransformedValueStore();
  useEffect(() => {
    const unsubscribe = useStoreBase.subscribe(
      state => state.store[cookieName] as Value | undefined,
      callback,
    );
    return unsubscribe;
  }, [callback, cookieName, useStoreBase]);
}

const getTransformRawValue = <Value>({
  cookieName,
  rawValue,
  transformRawValue,
}: {
  cookieName: string;
  rawValue?: string;
  transformRawValue: (value: string) => Value | undefined;
}): Value | undefined => {
  let value: undefined | Value;
  try {
    if (rawValue) {
      value = transformRawValue(rawValue);
    }
  } catch (error) {
    Log.warn(
      '@aboutyou/storage',
      `Cannot parse value from cookie ${cookieName}`,
      {error: error as Error},
    );
  }

  return value;
};
